import { CloudDownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { Alert, Button, Descriptions, Empty, InputNumber, Segmented, Space } from 'antd';
import { debounce } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router-dom';
import SmallSection from '../../../components/design/small-section.jsx';
import AddressSelect from '../../../components/inputs/AddressSelect.jsx';
import Currency from '../../../components/inputs/Currency.jsx';
import { FieldWrapper, GeneralFieldWrapper, InputWrapper } from '../../../components/inputs/input-wrappers.jsx';
// import { generateRouteUrl } from '../../../library/constants/routes.js';
import { Box } from '../../../components/design/box.js';
import Confirm from '../../../components/message/Confirm.jsx';
import { defaultProductMargin } from '../../../library/constants/defaults.js';
import { ERP_CURRENCY } from '../../../library/constants/dynamic.js';
import { convertDataTypes, formatDeliveryAddress } from '../../../library/helpers/index.js';
import { formDataToNestedJson } from '../../../library/utilities/formdata.js';
import { formatDateTime } from '../../../library/utilities/intl.js';
import { getFilePath } from '../../../library/utilities/urls.js';
import deliveryStyles from './project-item-update.delivery.module.scss';

const getFormattedAddress = (address) => formatDeliveryAddress(address)
const DeliveryAddressRow = ({ addressRow, currency, customerId, selectedAddressIds = [], onDelete = null, onChange, disabled }) => {
    const fetcher = useFetcher()
    const deleteFetcher = useFetcher()
    const { errors = null } = fetcher.data || {}

    // const isLoading = fetcher.state === 'submitting'

    const optimisticData = {
        ...addressRow,
        ...(addressRow.margin == null && { margin: defaultProductMargin, }) //== checks null as well as undefined
        // quantity: parseInt(fetcher.formData?.get('quantity')) ?? null,
        // address_id: parseInt(fetcher.formData?.get('address_id')) ?? null,
        // additional_cost: parseFloat(fetcher.formData?.get('additional_cost')),
        // margin: parseFloat(fetcher.formData?.get('margin')),
    }

    const handleSubmit = (form) => {
        const newData = convertDataTypes(formDataToNestedJson(new FormData(form)), {
            additional_cost: parseFloat,
            address_id: parseInt,
            deliveryId: parseInt,
            margin: parseFloat,
            quantity: parseInt,
            sales_price: parseFloat,
        })
        onChange?.(newData)
        fetcher.submit(form, { method: 'post' })
    }
    const debounceSubmit = useCallback(debounce(handleSubmit, 500), []);

    const handleRowDelete = (id) => {
        if (id) {
            deleteFetcher.submit({
                action: 'removeDeliveryAddress',
                deliveryId: id
            }, {
                method: 'post',
                encType: 'application/json'
            })
        }
        if (onDelete) {
            onDelete(id)
        }
    }

    return (
        <fetcher.Form onChange={(e) => {
            //Ignore nested forms change events (inside address select form)
            if (!e.currentTarget.contains(e.target)) {
                return false
            }
            debounceSubmit(e.currentTarget)
        }}>
            <input type="hidden" name="action" value={optimisticData.id ? 'updateDeliveryAddress' : 'addDeliveryAddress'} />
            {!!optimisticData.id && <input type="hidden" name="deliveryId" value={optimisticData.id} />}
            {/* <div className={`${isLoading ? deliveryStyles.deliveryRowLoading : deliveryStyles.deliveryRow}`}> */}
            <div className={deliveryStyles.deliveryRow}>
                <FieldWrapper name="quantity" errors={errors}>
                    <InputNumber key="quantity" name="quantity" defaultValue={optimisticData.quantity} controls={false} keyboard={false} disabled={disabled} />
                </FieldWrapper>
                <FieldWrapper name="address_id" errors={errors}>
                    <AddressSelect
                        name="address_id"
                        customerId={customerId}
                        onSelect={() => { }}
                        defaultValue={optimisticData.address_id}
                        excludeAddressIds={selectedAddressIds}
                        renderSelected={(address) => (
                            <div className={`ant-input dropdown ${disabled ? 'ant-input-disabled' : ''}`}>{address ? getFormattedAddress(address) : 'Select One'}</div>
                        )}
                        disabled={disabled}
                    />
                </FieldWrapper>
                <FieldWrapper name="additional_cost" errors={errors}>
                    <Currency key="additional_cost" name="additional_cost" currency={currency} defaultValue={optimisticData.additional_cost ?? null} controls={false} disabled={disabled} />
                </FieldWrapper>
                <FieldWrapper name="margin" errors={errors}>
                    <InputNumber
                        key="margin"
                        name="margin"
                        defaultValue={optimisticData.margin}
                        className='inputfield--percentage'
                        controls={false}
                        step="0.01"
                        precision="2"
                        min={0}
                        onFocus={event => event.target.select()}
                        max={'10000000000'}
                        disabled={disabled}
                    />
                </FieldWrapper>
                <FieldWrapper name="sales_price" errors={errors}>
                    <Currency key="sales_price" name="sales_price" currency={currency} value={optimisticData.sales_price} disabled controls={false} />
                </FieldWrapper>
                <Confirm
                    content="Are you sure you want to remove this delivery address ?"
                    onConfirm={() => handleRowDelete(optimisticData?.id)}
                    icon={<DeleteOutlined />}
                    loading={deleteFetcher.state !== 'idle'}
                    shape="square"
                    type="default"
                    className="ant-btn-actionpanel"
                    disabled={disabled || selectedAddressIds.length <= 1}
                    danger
                    ghost
                />
            </div>
        </fetcher.Form>
    )
}
DeliveryAddressRow.propTypes = {
    deliveryId: PropTypes.number,
    addressRow: PropTypes.object.isRequired,
    customerId: PropTypes.number.isRequired,
    currency: PropTypes.oneOf(['USD', 'GBP', 'EUR']).isRequired,
    selectedAddressIds: PropTypes.arrayOf(PropTypes.number),
    onAfterSubmit: PropTypes.func,
    onDelete: PropTypes.func,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
}

const NormalDeliveryAddresses = ({ deliveryAddresses, customerId, orderQuantity, disabled }) => {
    const addNewfetcher = useFetcher()
    // const [totalQuantity, setTotalQuantity] = useState(deliveryAddresses?.reduce(({ quantity }, acc) => quantity + acc, 0)) 
    const [addressQuantities, setAddressQuantities] = useState(
        deliveryAddresses?.reduce((acc, { address_id, quantity }) => {
            acc[address_id] = quantity;
            return acc;
        }, {})
    );
    useEffect(() => {
        setAddressQuantities(deliveryAddresses?.reduce((acc, { address_id, quantity }) => {
            acc[address_id] = quantity;
            return acc;
        }, {}))
    }, [deliveryAddresses])
    const sortedDeliveryAddresses = useMemo(() => deliveryAddresses?.filter(({ type }) => type !== 3)?.sort((a, b) => a.id - b.id), [deliveryAddresses])
    const totalQuantity = useMemo(() => Object.values(addressQuantities)?.reduce((acc, q) => q + acc, 0), [addressQuantities])
    const isNewLoading = addNewfetcher.formData?.get('action') === 'addDeliveryAddress'

    if (!sortedDeliveryAddresses || sortedDeliveryAddresses.length === 0) {
        return (
            <>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No delivery addresses added yet" />
                <div className="actions align--right">
                    <AddressSelect
                        name="address_id"
                        customerId={customerId}
                        onSelect={(aid) => {
                            addNewfetcher.submit({
                                address_id: aid,
                                action: 'addDeliveryAddress'
                            }, {
                                method: 'post',
                            })
                        }}
                        excludeAddressIds={deliveryAddresses.map(({ address_id }) => address_id)}
                        renderSelected={() => (
                            <Button loading={isNewLoading} disabled={disabled}>Add Delivery</Button>
                        )}
                        disabled={disabled || isNewLoading}
                    />
                </div>
            </>
        )
    }

    return (
        <>
            {orderQuantity > 0 && totalQuantity !== orderQuantity && (
                <Alert
                    type="warning"
                    message={(totalQuantity - orderQuantity) > 0 ? "Total quantity exceeds order quantity" : "Total quantity is less than order quantity"}
                    showIcon
                />
            )}
            <br />
            <div className={deliveryStyles.deliveryRowHead}>
                <div>Quantity</div>
                <div>Delivery Address</div>
                <div>Delivery Cost</div>
                <div>Margin</div>
                <div>Total Price</div>
                <div></div>
            </div>
            {sortedDeliveryAddresses.map((address) => (
                <SmallSection key={address.id}>
                    <DeliveryAddressRow
                        addressRow={address}
                        currency={ERP_CURRENCY}
                        customerId={customerId}
                        selectedAddressIds={deliveryAddresses.map(({ address_id }) => address_id)} disabled={disabled}
                        onChange={({ address_id, quantity }) =>
                            setAddressQuantities((prevAQ) => ({
                                ...prevAQ,
                                [address_id]: quantity,
                            }))
                        }
                    />
                </SmallSection>
            ))}
            <div className="actions align--right">
                <AddressSelect
                    name="address_id"
                    customerId={customerId}
                    onSelect={(aid) => {
                        addNewfetcher.submit({
                            address_id: aid,
                            action: 'addDeliveryAddress'
                        }, {
                            method: 'post',
                        })
                    }}
                    excludeAddressIds={deliveryAddresses.map(({ address_id }) => address_id)}
                    renderSelected={() => (
                        <Button loading={isNewLoading} disabled={disabled}>Add Delivery</Button>
                    )}
                    disabled={disabled || isNewLoading}
                />
            </div>
        </>
    )
}
NormalDeliveryAddresses.propTypes = {
    deliveryAddresses: PropTypes.arrayOf(PropTypes.object).isRequired,
    customerId: PropTypes.number.isRequired,
    orderQuantity: PropTypes.number,
    disabled: PropTypes.bool,
}

const BulkDeliveryAddresses = ({ bulkAddressData, projectId }) => {
    // const { itemDetails, currency, project } = useLoaderData()
    const fetcher = useFetcher()
    const fileInputRef = useRef(null);
    const deleteFetcher = useFetcher()
    const { errors = null } = fetcher.data || {}

    const [selectedFile, setSelectedFile] = useState(null);
    const [showUploadedFile, setShowUploadedFile] = useState(!!bulkAddressData?.bulk_address_file);
    const [additionalCost, setAdditionalCost] = useState(bulkAddressData.additional_cost ?? 0);
    const [margin, setMargin] = useState(bulkAddressData.margin ?? 0);
    const [salesPrice, setSalesPrice] = useState(bulkAddressData.sales_price ?? 0);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    useEffect(() => {
        const cost = parseFloat(additionalCost) || 0;
        const marginPercent = parseFloat(margin) || 0;
        const total = cost + (cost * marginPercent / 100);
        setSalesPrice(Number(total.toFixed(2)));
    }, [additionalCost, margin]);


    useEffect(() => {
        if (fetcher.state === 'idle' && fetcher.data === true) {
            setSelectedFile(null);
            setShowUploadedFile(true);
            fileInputRef.current.value = null;
        }
    }, [fetcher.state, fetcher.data]);

    return (
        <fetcher.Form method="post" encType='multipart/form-data'>
            {showUploadedFile && bulkAddressData?.bulk_address_file && (
                <Descriptions layout="vertical">
                    <Descriptions.Item label="Uploaded File">
                        <CloudDownloadOutlined />&nbsp;
                        <a href={getFilePath(bulkAddressData.bulk_address_file?.url, projectId)}>
                            {bulkAddressData.bulk_address_file?.name}
                        </a>
                    </Descriptions.Item>
                    <Descriptions.Item label="Uploaded By">
                        {bulkAddressData.bulk_address_file?.uploaded_by}
                    </Descriptions.Item>
                    <Descriptions.Item label="Uploaded At">
                        {formatDateTime(bulkAddressData.bulk_address_file?.uploaded_time)}
                    </Descriptions.Item>
                </Descriptions>
            )}

            <SmallSection header={
                <div className={deliveryStyles.deliveryRowHead} style={{ width: '100%' }}>
                    <div>File</div>
                    <div>Notes</div>
                    <div>Cost</div>
                    <div>Margin</div>
                    <div>Total Price</div>
                    <div>Download File</div>
                </div>
            }>
                <div className={deliveryStyles.deliveryRow}>
                    <FieldWrapper name="file_id" errors={errors} >
                        <input type="file" accept=".xls,.xlsx,.csv,.ods" name="file_id" onChange={handleFileChange} style={{ padding: "10px 0 0 0", border: "none" }} ref={fileInputRef}
                        />
                    </FieldWrapper>
                    <InputWrapper name="notes" defaultValue={bulkAddressData.notes} errors={errors} textarea allowClear />
                    <FieldWrapper name="additional_cost" errors={errors}>
                        <Currency
                            key="additional_cost"
                            name="additional_cost"
                            currency={ERP_CURRENCY}
                            value={additionalCost}
                            onChange={(val) => setAdditionalCost(val)}
                            controls={false}
                        />
                    </FieldWrapper>
                    <FieldWrapper name="margin" errors={errors}>
                        <InputNumber
                            key="margin"
                            name="margin"
                            value={margin}
                            className='inputfield--percentage'
                            controls={false}
                            step="0.01"
                            precision="2"
                            min={0}
                            max={10000000000}
                            onChange={(val) => setMargin(val)}
                            onFocus={event => event.target.select()}
                        />
                    </FieldWrapper>
                    <FieldWrapper name="sales_price" errors={errors}>
                        <Currency
                            key="sales_price"
                            name="sales_price"
                            currency={ERP_CURRENCY}
                            value={salesPrice}
                            disabled
                            controls={false}
                        />
                    </FieldWrapper>
                    <div className="">
                        {!!bulkAddressData.bulk_address_file?.url && (
                            <Space>
                                <Button
                                    icon={<CloudDownloadOutlined />}
                                    href={getFilePath(bulkAddressData.bulk_address_file.url, projectId)}
                                />
                                <Confirm
                                    content="Are you sure to delete this line?"
                                    okText="Yes"
                                    icon={<DeleteOutlined />}
                                    shape="square"
                                    type="default"
                                    className="ant-btn-actionpanel"
                                    onConfirm={() => {
                                        deleteFetcher.submit(
                                            {
                                                action: "removeBulkDeliveryFile",
                                                file_id: bulkAddressData.id,
                                            },
                                            {
                                                method: "DELETE",
                                                encType: "application/json",
                                            }
                                        );

                                        setSelectedFile(null)
                                        fileInputRef.current.value = "";
                                    }}
                                    danger
                                    ghost
                                />
                            </Space>
                        )}
                    </div>
                </div>
                <p style={{ marginTop: "5px" }}>Supported formats : XLS, XLSX, CSV, ODS </p>
                <p style={{ marginTop: "-10px" }}>Max file size : 5 MB</p>
                {selectedFile &&
                    <div
                        style={{
                            marginTop: 8,
                            padding: '5px 10px',
                            fontStyle: 'italic',
                            color: '#333',
                            borderRadius: '12px',
                            minWidth: '30%',
                            width: '40%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            border: "1px solid #ddd"
                        }}
                    >
                        <span style={{ flex: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Selected File : {selectedFile.name}
                        </span>
                        <Button className="cancelButton" icon={<DeleteOutlined />} shape='default' onClick={() => {
                            setSelectedFile(null)
                            setShowUploadedFile(false)
                            fileInputRef.current.value = "";
                        }} />
                    </div>
                }

            </SmallSection>
            <div className="actions">
                <Button
                    type="primary"
                    htmlType='submit'
                    name="action" value='saveBulkDeliveryAddress'
                    loading={fetcher.state !== 'idle'}
                >Submit</Button>
            </div>
        </fetcher.Form>
    )
}
BulkDeliveryAddresses.propTypes = {
    projectId: PropTypes.number.isRequired,
    bulkAddressData: PropTypes.object.isRequired
}

const DeliveryAddresses = ({ itemDetails, projectId, customerId, disabled }) => {
    const [deliveryType, setDeliveryType] = useState(itemDetails.delivery_type === 3 ? 'bulk' : 'normal')
    const orderQuantity = useMemo(() => itemDetails.approved_quantity ? itemDetails.approved_quantity : itemDetails.quantities?.length === 1 ? itemDetails.quantities[0] : null, [itemDetails])

    return (
        <>
            <Box header={
                <GeneralFieldWrapper label="Delivery Type" horizontal>
                    <Segmented
                        options={[{
                            value: 'normal',
                            label: 'Single/Multiple'
                        }, {
                            value: 'bulk',
                            label: 'Bulk Delivery Addresses'
                        }]}
                        value={deliveryType}
                        onChange={(value) => {
                            setDeliveryType(value)
                        }}
                        disabled={disabled}
                    />
                </GeneralFieldWrapper>
            }>
                {deliveryType === 'bulk' ? (
                    <BulkDeliveryAddresses bulkAddressData={itemDetails.item_delivery_addresses?.[0] ?? {}} projectId={projectId} disabled={disabled} />
                ) : (
                    <NormalDeliveryAddresses
                        orderQuantity={orderQuantity}
                        deliveryAddresses={itemDetails.item_delivery_addresses}
                        customerId={customerId}
                        disabled={disabled}
                    />
                )}
            </Box>
        </>
    )
}
DeliveryAddresses.propTypes = {
    itemDetails: PropTypes.object.isRequired,
    projectId: PropTypes.number.isRequired,
    customerId: PropTypes.number.isRequired,
    disabled: PropTypes.bool,
}

export { DeliveryAddresses };
